import type { TradeGoodId } from './market';

/**
 * Outposts on neutral bodies (GDD §5): a transport settles a site and
 * becomes the station. Each gives a zone of control and a steady output.
 * Prices are in ГС, paid in the player's currency. Starting values for tuning.
 */
export const OUTPOST_KINDS = ['miningStation', 'gasHarvester', 'lagrangeStation'] as const;
export type OutpostKind = (typeof OUTPOST_KINDS)[number];

export interface OutpostDef {
  readonly id: OutpostKind;
  /** i18n key: `outpost.<id>.name`. */
  readonly i18nKey: `outpost.${OutpostKind}`;
  /** Zone of control radius, hexes (GDD §5: 1–2). */
  readonly zoneRadius: number;
  /** Units per cycle; a mining station takes its body's deposit, the others a fixed good. */
  readonly output: number;
  readonly good: TradeGoodId | 'deposit';
  /** Paid on top of the transport, which becomes the station. */
  readonly cost: { readonly cash: number; readonly alloys: number };
  readonly buildCycles: number;
  /** Base upkeep per cycle, ГС; × N^exponent for N outposts (GDD §10: upkeep outgrows the empire). */
  readonly upkeep: number;
}

export const OUTPOSTS: Readonly<Record<OutpostKind, OutpostDef>> = {
  miningStation: {
    id: 'miningStation',
    i18nKey: 'outpost.miningStation',
    zoneRadius: 1,
    output: 6,
    good: 'deposit',
    cost: { cash: 150, alloys: 10 },
    buildCycles: 2,
    upkeep: 4,
  },
  gasHarvester: {
    id: 'gasHarvester',
    i18nKey: 'outpost.gasHarvester',
    zoneRadius: 1,
    output: 8,
    good: 'helium3',
    cost: { cash: 150, alloys: 10 },
    buildCycles: 2,
    upkeep: 4,
  },
  lagrangeStation: {
    id: 'lagrangeStation',
    i18nKey: 'outpost.lagrangeStation',
    zoneRadius: 2,
    output: 2,
    good: 'exotics',
    cost: { cash: 250, alloys: 20 },
    buildCycles: 3,
    upkeep: 6,
  },
};

export const OUTPOST_RULES = {
  /** Outposts a player may hold by spaceport level 1/2/3 (GDD §5). */
  limitBySpaceportLevel: [2, 4, 6],
  /** Upkeep of every outpost × N^exponent, N = outposts held. */
  upkeepExponent: 0.3,
} as const;
